import React, { useState, useEffect } from 'react'
import {Search, Briefcase, GraduationCap, Code, Clock, MapPin, Building2, DollarSign, ChevronRight, Users, FileText, CheckCircle, HelpCircle, ChevronDown} from 'lucide-react'
import { useJobs } from './hooks/useJobs'

function App() {
  const [searchQuery, setSearchQuery] = useState('')
  const [selectedCategory, setSelectedCategory] = useState('all')
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(null)
  const { jobs, loading, fetchFeaturedJobs, fetchJobsByCategory } = useJobs()

  const categories = [
    { id: 'teaching', name: 'Teaching', icon: GraduationCap, color: 'bg-blue-500', count: 145 },
    { id: 'technology', name: 'Technology', icon: Code, color: 'bg-green-500', count: 203 },
    { id: 'parttime', name: 'Part-time', icon: Clock, color: 'bg-orange-500', count: 87 }
  ]

  // Fetch jobs when category changes
  useEffect(() => {
    if (selectedCategory === 'all') {
      fetchFeaturedJobs()
    } else {
      fetchJobsByCategory(selectedCategory)
    }
  }, [selectedCategory])

  const howItWorks = [
    { step: 1, title: 'Create Your Profile', description: 'Sign up and showcase your skills and experience', icon: Users },
    { step: 2, title: 'Browse Jobs', description: 'Search through hundreds of English-friendly positions', icon: Search },
    { step: 3, title: 'Apply Directly', description: 'Submit applications to companies hiring foreigners', icon: FileText },
    { step: 4, title: 'Get Hired', description: 'Start your new career in Tokyo', icon: CheckCircle }
  ]

  const faqs = [
    {
      question: 'Can foreigners find jobs in Tokyo?',
      answer: 'Yes! Tokyo has thousands of jobs for foreigners and English speakers. Many companies actively hire expats for teaching positions, IT roles, and international business. JobsTokyo.com specializes in connecting foreign job seekers with English-friendly employers.'
    },
    {
      question: 'Do I need to speak Japanese to work in Tokyo?',
      answer: 'Not necessarily. Many positions, especially in IT, software engineering, and English teaching, don\'t require Japanese language skills. However, learning basic Japanese can improve your job prospects and daily life in Tokyo.'
    },
    {
      question: 'What types of jobs are available for English speakers in Tokyo?',
      answer: 'The most common jobs include English teaching positions at conversation schools, IT and software engineer roles at tech companies, customer support for international companies, content creation, and part-time positions in hospitality and retail.'
    },
    {
      question: 'Can Americans work in Tokyo?',
      answer: 'Yes, Americans can work in Tokyo with the proper work visa. Common visa types include the Instructor visa for teaching, Engineer/Specialist in Humanities visa for IT and business roles, and Working Holiday visa for those aged 18-30.'
    },
    {
      question: 'What is the average salary for English teaching jobs in Tokyo?',
      answer: 'English teachers in Tokyo typically earn ¥250,000-¥320,000 per month at conversation schools. Private tutors can earn ¥3,000-¥5,000 per hour. International schools and universities offer higher salaries ranging from ¥350,000-¥600,000+ per month.'
    },
    {
      question: 'How much do software engineers make in Tokyo?',
      answer: 'Software engineers in Tokyo earn between ¥4 million to ¥12 million per year depending on experience. Senior developers and those at international tech companies can earn significantly more, often ¥8-15 million annually with benefits and stock options.'
    },
    {
      question: 'Do companies in Tokyo sponsor work visas for foreigners?',
      answer: 'Yes, many companies in Tokyo sponsor work visas, especially for teaching and IT positions. Look for job postings that specifically mention "visa sponsorship available" on JobsTokyo.com to find employers who support foreign workers.'
    },
    {
      question: 'What are the best areas in Tokyo for expats to find work?',
      answer: 'Popular areas include Shibuya, Shinjuku, Roppongi, and Minato for IT and international business jobs. For English teaching, opportunities are spread throughout Tokyo. Part-time work is abundant in tourist areas like Shibuya, Harajuku, and Asakusa.'
    },
    {
      question: 'How long does it take to find a job in Tokyo as a foreigner?',
      answer: 'The timeline varies, but most job seekers find positions within 1-3 months. English teaching positions can be secured quickly, while specialized IT roles may take longer. Having a work visa already can speed up the process significantly.'
    },
    {
      question: 'Are part-time jobs in Tokyo suitable for students and working holiday visa holders?',
      answer: 'Yes! Tokyo has many part-time opportunities perfect for students and working holiday visa holders. Common options include English tutoring, cafe/restaurant work, retail, and content creation. Many positions offer flexible schedules and don\'t require Japanese fluency.'
    }
  ]

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-2">
              <Briefcase className="w-8 h-8 text-blue-600" />
              <span className="text-2xl font-bold text-gray-900">Jobs<span className="text-blue-600">Tokyo</span></span>
            </div>
            <nav className="hidden md:flex items-center gap-6">
              <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Find Jobs</a>
              <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">For Employers</a>
              <a href="#" className="text-gray-600 hover:text-blue-600 transition-colors">Resources</a>
              <button className="px-4 py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                Sign In
              </button>
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Post a Job
              </button>
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6 leading-tight">
              Find Your Dream Job in <span className="text-blue-600">Tokyo</span>
            </h1>
            <p className="text-xl text-gray-600 mb-4">
              The #1 Job Board for English Speakers & Expats in Japan
            </p>
            <p className="text-lg text-gray-500 mb-10">
              Discover teaching jobs, IT positions, software engineer roles, and part-time work opportunities in Tokyo. 
              Connect with companies actively hiring foreigners and English speakers.
            </p>

            {/* Search Bar */}
            <div className="bg-white rounded-2xl shadow-xl p-4 sm:p-6 max-w-3xl mx-auto">
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="text"
                    placeholder="Job title, keywords, or company"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <button className="bg-blue-600 text-white px-8 py-3 rounded-lg hover:bg-blue-700 transition-colors font-semibold flex items-center justify-center gap-2">
                  Search Jobs
                  <ChevronRight className="w-5 h-5" />
                </button>
              </div>
              <div className="mt-4 flex flex-wrap gap-2">
                <span className="text-sm text-gray-500">Popular:</span>
                <button className="text-sm text-blue-600 hover:underline">English Teacher</button>
                <span className="text-gray-300">•</span>
                <button className="text-sm text-blue-600 hover:underline">Software Engineer</button>
                <span className="text-gray-300">•</span>
                <button className="text-sm text-blue-600 hover:underline">Part-time</button>
                <span className="text-gray-300">•</span>
                <button className="text-sm text-blue-600 hover:underline">IT Jobs</button>
              </div>
            </div>

            {/* Stats */}
            <div className="mt-12 grid grid-cols-3 gap-6 max-w-2xl mx-auto">
              <div>
                <div className="text-3xl font-bold text-blue-600">435+</div>
                <div className="text-sm text-gray-600">Active Jobs</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">150+</div>
                <div className="text-sm text-gray-600">Companies</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-blue-600">10k+</div>
                <div className="text-sm text-gray-600">Job Seekers</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Job Categories */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Browse Jobs by Category
            </h2>
            <p className="text-lg text-gray-600">
              Find opportunities in teaching, technology, and flexible part-time positions
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {categories.map((category) => {
              const Icon = category.icon
              return (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className="bg-white border-2 border-gray-200 rounded-2xl p-8 hover:border-blue-500 hover:shadow-lg transition-all duration-300 text-left group"
                >
                  <div className={`${category.color} w-14 h-14 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">{category.name}</h3>
                  <p className="text-gray-600 mb-4">
                    {category.id === 'teaching' && 'English teaching positions at conversation schools and private tutoring'}
                    {category.id === 'technology' && 'IT jobs, software engineering, and tech roles for English speakers'}
                    {category.id === 'parttime' && 'Flexible part-time work opportunities with adjustable schedules'}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-blue-600 font-semibold">{category.count} open positions</span>
                    <ChevronRight className="w-5 h-5 text-gray-400 group-hover:text-blue-600 group-hover:translate-x-1 transition-all" />
                  </div>
                </button>
              )
            })}
          </div>
        </div>
      </section>

      {/* Featured Jobs */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Featured Jobs in Tokyo</h2>
              <p className="text-gray-600">Hand-picked opportunities for English speakers and expats</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setSelectedCategory('all')}
                className={`px-4 py-2 rounded-lg transition-colors ${selectedCategory === 'all' ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
              >
                All
              </button>
              {categories.map(cat => (
                <button
                  key={cat.id}
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`hidden sm:block px-4 py-2 rounded-lg transition-colors ${selectedCategory === cat.id ? 'bg-blue-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-100'}`}
                >
                  {cat.name}
                </button>
              ))}
            </div>
          </div>

          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <p className="mt-4 text-gray-600">Loading jobs...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-12 col-span-full">
              <p className="text-gray-600 text-lg">No jobs found in this category.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobs.map((job) => (
              <div key={job._id} className="bg-white rounded-xl border border-gray-200 p-6 hover:shadow-lg hover:border-blue-300 transition-all duration-300 cursor-pointer group">
                <div className="flex items-start justify-between mb-4">
                  <div className="bg-blue-50 w-12 h-12 rounded-lg flex items-center justify-center group-hover:bg-blue-100 transition-colors">
                    <Building2 className="w-6 h-6 text-blue-600" />
                  </div>
                  <span className="text-xs font-semibold px-3 py-1 bg-green-100 text-green-700 rounded-full">
                    {job.type}
                  </span>
                </div>
                
                <h3 className="text-xl font-bold text-gray-900 mb-2 group-hover:text-blue-600 transition-colors">
                  {job.title}
                </h3>
                <p className="text-gray-600 font-medium mb-4">{job.companyName}</p>
                
                <div className="space-y-2 mb-4">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <MapPin className="w-4 h-4" />
                    {job.location}
                  </div>
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <DollarSign className="w-4 h-4" />
                    {job.salary}
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-4">
                  {job.tags.map((tag, idx) => (
                    <span key={idx} className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded">
                      {tag}
                    </span>
                  ))}
                </div>

                <button className="w-full py-2 text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-600 hover:text-white transition-colors font-semibold">
                  View Details
                </button>
              </div>
              ))}
            </div>
          )}

          <div className="text-center mt-10">
            <button className="px-8 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold inline-flex items-center gap-2">
              View All Jobs
              <ChevronRight className="w-5 h-5" />
            </button>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              How JobsTokyo Works
            </h2>
            <p className="text-lg text-gray-600">
              Your journey to work in Tokyo starts here
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            {howItWorks.map((item) => {
              const Icon = item.icon
              return (
                <div key={item.step} className="text-center">
                  <div className="relative mb-6">
                    <div className="bg-blue-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto">
                      <Icon className="w-10 h-10 text-blue-600" />
                    </div>
                    <div className="absolute -top-2 -right-2 bg-blue-600 text-white w-8 h-8 rounded-full flex items-center justify-center font-bold text-sm mx-auto left-0 right-0">
                      {item.step}
                    </div>
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{item.title}</h3>
                  <p className="text-gray-600">{item.description}</p>
                </div>
              )
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Ready to Start Your Tokyo Career?
          </h2>
          <p className="text-xl text-blue-100 mb-8">
            Join thousands of English speakers and expats who found their dream jobs in Tokyo
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="px-8 py-4 bg-white text-blue-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold text-lg">
              Browse All Jobs
            </button>
            <button className="px-8 py-4 bg-blue-800 text-white rounded-lg hover:bg-blue-900 transition-colors font-semibold text-lg border-2 border-white">
              Create Free Account
            </button>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4">
              <HelpCircle className="w-8 h-8 text-blue-600" />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Everything you need to know about finding jobs in Tokyo as an English speaker
            </p>
          </div>

          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className="bg-white border-2 border-gray-200 rounded-xl overflow-hidden hover:border-blue-300 transition-colors"
              >
                <button
                  onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)}
                  className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                >
                  <h3 className="text-lg font-semibold text-gray-900 pr-8">
                    {faq.question}
                  </h3>
                  <ChevronDown
                    className={`w-5 h-5 text-blue-600 flex-shrink-0 transition-transform duration-200 ${openFaqIndex === index ? 'rotate-180' : ''}`}
                  />
                </button>
                {openFaqIndex === index && (
                  <div className="px-6 pb-5 pt-0">
                    <p className="text-gray-600 leading-relaxed">
                      {faq.answer}
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>

          <div className="mt-10 text-center bg-blue-50 rounded-xl p-8">
            <h3 className="text-xl font-bold text-gray-900 mb-2">
              Still have questions?
            </h3>
            <p className="text-gray-600 mb-4">
              Our support team is here to help you find the perfect job in Tokyo
            </p>
            <button className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-semibold">
              Contact Support
            </button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-gray-300 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Briefcase className="w-6 h-6 text-blue-500" />
                <span className="text-xl font-bold text-white">JobsTokyo</span>
              </div>
              <p className="text-sm">
                Connecting English speakers and expats with amazing job opportunities in Tokyo, Japan.
              </p>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Job Seekers</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Browse Jobs</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Teaching Jobs</a></li>
                <li><a href="#" className="hover:text-white transition-colors">IT Jobs Tokyo</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Part-time Work</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Resources</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">Living in Tokyo</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Visa Information</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Career Advice</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Blog</a></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold text-white mb-4">Company</h4>
              <ul className="space-y-2 text-sm">
                <li><a href="#" className="hover:text-white transition-colors">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-colors">For Employers</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Contact</a></li>
                <li><a href="#" className="hover:text-white transition-colors">Privacy Policy</a></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-gray-800 pt-8 text-sm text-center">
            <p>© 2024 JobsTokyo.com. All rights reserved. Find jobs in Tokyo for English speakers, expats, and foreigners.</p>
          </div>
        </div>
      </footer>
    </div>
  )
}

export default App
